import React, { FC, useCallback, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { Grid, Typography } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { useRefresh, useRefreshTransactions, useRest } from '../../../../../components/Context'
import { Loader } from '../../../../../components/Loader'
import { InputField } from '../../../../../components/Dashboard/InputField'
import { InputSelect } from '../../../../../components/Dashboard/InputSelect'
import { GreenButton } from '../../../../../components/Dashboard/Buttons/GreenButton'
import { resolver } from './resolver'
import { GridRow } from '../../../../../components/GridRow'

const useStyles = makeStyles((theme: Theme) => ({
  formWrap: {
    display: 'flex',
    width: '100%',
    height: '100%',
    flexDirection: 'column',
    paddingTop: theme.spacing(4),
  },
  container: {
    minHeight: 40,
    maxHeight: 40,
    paddingBottom: theme.spacing(2),
  },
  sectionWrap: {
    width: '100%',
    height: '100%',
    padding: theme.spacing(4, 4, 4, 4),
    background: 'rgb(49,52,58)',
  },
  inputWrap: {
    height: 76,
  },
  button: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(2),
  },
}))

type Inputs = {
  asset: string
  amount: string
}

interface StakeSectionProps {
  title: string
  assets: Array<string>
}

export const Stake: FC<StakeSectionProps> = ({ title, assets }) => {
  const formMethods = useForm<Inputs>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldFocusError: true,
    resolver,
  })
  const classes = useStyles()
  const { rxiosAuth } = useRest()
  const { saveIsRefresh } = useRefresh()
  const { saveIsRefreshTransactions } = useRefreshTransactions()
  const { handleSubmit } = formMethods
  const [loading, setLoading] = useState<boolean>(true)
  // eslint-disable-next-line
  const [enums, setEnums] = useState<any>({})

  const enumsList = useCallback(() => {
    setEnums(assets.map((asset) => ({ value: asset, label: asset })))
    setLoading(false)
  }, [assets])

  useEffect(() => {
    if (assets) {
      enumsList()
    }
    // eslint-disable-next-line
  }, [assets])

  const onSubmit = useCallback(
    async (data: Inputs) => {
      let cleanupFunction = false
      setLoading(true)

      rxiosAuth.post('staking/stake', data).subscribe(
        // eslint-disable-next-line
        (next: any) => {
          saveIsRefresh(true)
          saveIsRefreshTransactions(true)
          formMethods.reset()
          if (!cleanupFunction) setLoading(false)
        },
        (error) => {
          toast.error(error.response.data.message)
          setLoading(false)
        },
        () => {
          toast.success('Stake was added successfully')
          setLoading(false)
        },
      )
      cleanupFunction = true
    },
    // eslint-disable-next-line
    [rxiosAuth],
  )

  return (
    <>
      {loading && <Loader />}

      <Grid item xs={12} className={classes.formWrap}>
        <FormProvider {...formMethods}>
          <Grid item xs={12} className={classes.container}>
            <Typography variant={'h6'}>{title}</Typography>
          </Grid>

          <Grid item xs={12} className={classes.sectionWrap}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <GridRow spacing={2} className={classes.inputWrap}>
                <Grid item xs={6}>
                  <InputSelect inputName="asset" placeholder="Please select" width={'100%'} enums={enums} />
                </Grid>
                <Grid item xs={6}>
                  <InputField inputName="amount" placeholder="Amount" inputType="text" width={'100%'} />
                </Grid>
              </GridRow>

              <Grid item xs={12} className={classes.button}>
                <GreenButton text={'Stake'} width={296} />
              </Grid>
            </form>
          </Grid>
        </FormProvider>
      </Grid>
    </>
  )
}
